import { c as _c } from "react/compiler-runtime";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { TimeSeriesDataPoint } from "@/hooks/useMetricsTimeSeries";
import type { FailureRateData } from "@/hooks/useMetricsFailureRate";
import type { TopError } from "@/hooks/useMetricsTopErrors";
import type { RecentActivity } from "@/hooks/useMetricsRecentActivity";
export interface MetricsSummaryData {
  runsTimeSeries: {
    granularity: string;
    data: TimeSeriesDataPoint[];
  };
  runsTimeSeriesError?: string;
  failureRate: FailureRateData | null;
  failureRateError?: string;
  topErrors: TopError[];
  topErrorsError?: string;
  recentActivity: RecentActivity[];
  recentActivityError?: string;
}
interface MetricsSummaryApiResponse {
  code: number;
  message: string;
  data: MetricsSummaryData;
  requestId?: string;
}
interface UseMetricsSummaryOptions {
  from: number;
  to: number;
  granularity: "hourly" | "daily";
  topErrorsLimit?: number;
  recentActivityLimit?: number;
  status?: "success" | "failure" | "running";
  enabled?: boolean;
}

/**
 * Fetches runs time-series, failure rate, top errors, and recent activity
 * in a single request. The backend runs the four sub-queries in parallel.
 */
export function useMetricsSummary(t0) {
  const $ = _c(18);
  const {
    from,
    to,
    granularity,
    topErrorsLimit: t1,
    recentActivityLimit: t2,
    status,
    enabled: t3
  } = t0;
  const topErrorsLimit = t1 === undefined ? 10 : t1;
  const recentActivityLimit = t2 === undefined ? 50 : t2;
  const enabled = t3 === undefined ? true : t3;
  const t4 = status ?? null;
  let t5;
  if ($[0] !== from || $[1] !== granularity || $[2] !== recentActivityLimit || $[3] !== t4 || $[4] !== to || $[5] !== topErrorsLimit) {
    t5 = ["metrics-summary", {
      from,
      to,
      granularity,
      topErrorsLimit,
      recentActivityLimit,
      status: t4
    }];
    $[0] = from;
    $[1] = granularity;
    $[2] = recentActivityLimit;
    $[3] = t4;
    $[4] = to;
    $[5] = topErrorsLimit;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== from || $[8] !== granularity || $[9] !== recentActivityLimit || $[10] !== status || $[11] !== to || $[12] !== topErrorsLimit) {
    t6 = async () => {
      const params = new URLSearchParams();
      params.set("from", String(from));
      params.set("to", String(to));
      params.set("granularity", granularity);
      params.set("topErrorsLimit", String(topErrorsLimit));
      params.set("recentActivityLimit", String(recentActivityLimit));
      if (status) {
        params.set("status", status);
      }
      const url = `${V2_ENDPOINT}/v1/metrics/summary?${params.toString()}`;
      const response = await authenticatedFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch metrics summary: ${response.statusText}`);
      }
      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(result.message || "Failed to fetch metrics summary");
      }
      return {
        runsTimeSeries: {
          granularity: result.data?.runsTimeSeries?.granularity ?? granularity,
          data: Array.isArray(result.data?.runsTimeSeries?.data) ? result.data.runsTimeSeries.data : []
        },
        runsTimeSeriesError: result.data?.runsTimeSeriesError,
        failureRate: result.data?.failureRate ?? null,
        failureRateError: result.data?.failureRateError,
        topErrors: Array.isArray(result.data?.topErrors) ? result.data.topErrors : [],
        topErrorsError: result.data?.topErrorsError,
        recentActivity: Array.isArray(result.data?.recentActivity) ? result.data.recentActivity : [],
        recentActivityError: result.data?.recentActivityError
      };
    };
    $[7] = from;
    $[8] = granularity;
    $[9] = recentActivityLimit;
    $[10] = status;
    $[11] = to;
    $[12] = topErrorsLimit;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = enabled && typeof from === "number" && typeof to === "number";
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      staleTime: 30000
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return useQuery(t8);
}